/**
 * S-14 Today's Menu (V7, #28) — v3 "AFTER-DARK ARCADE" restyle.
 *
 * 4–6 heuristic picks with a one-line "why" chip, midnight-local refresh.
 * Acting on a card = normal swipe semantics (the shared useSwipeAction hook →
 * POST /swipes; a consumed or vanished pick flips to ©deck.card_gone and
 * drops).
 *
 * v3: the nightly tasting menu — courses numbered and plated one by one
 * (staggered entrance), the "why" is the chef's note, the countdown to the
 * next batch runs in Anton tabular digits. Loading and empty states are the
 * mascot working the kitchen, never a bare line.
 */
import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { resolveCopy } from "@humble/shared";
import { Button, Card, Mascot, rise, Sheet, Skeleton, stagger, toast } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { CardFace } from "../deck/deck.js";
import { useSwipeAction, type DeckCard, type SwipeResult } from "../deck/use-swipe.js";
import "../deck/deck.css";
import "./menu.css";

function Head() {
  return (
    <header className="menu__head">
      <p className="h-caps menu__kicker">Tonight&apos;s picks</p>
      <h1 className="type-display-3">Today&apos;s Menu</h1>
      <p className="type-editorial menu__lede">{resolveCopy("menu.lede")}</p>
      <span className="menu__countdown">
        Next batch in
        <span className="menu__countdown-time type-numeric">{midnightCountdown()}</span>
      </span>
    </header>
  );
}

export function MenuScreen() {
  const [picks, setPicks] = useState<DeckCard[] | null>(null);
  const [reveal, setReveal] = useState<SwipeResult | null>(null);
  const { send, busy } = useSwipeAction();

  const load = useCallback(async () => {
    try {
      setPicks((await api.get("/deck/menu")) as DeckCard[]);
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
      setPicks([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(card: DeckCard, verdict: "like" | "reject" | "crush") {
    const id = card.profile?.user?.id;
    if (!id || busy) return;
    const out = await send({ targetId: id, verdict });
    if (out.kind === "cap") {
      toast(resolveCopy("deck.cap_reached") ?? "Out of swipes.", "error");
      return;
    }
    if (out.kind === "error") return;
    setPicks((prev) => (prev ?? []).filter((c) => c.profile?.user?.id !== id));
    if (out.kind === "resolved" && out.result?.result.startsWith("matched_")) setReveal(out.result);
  }

  if (picks === null) {
    return (
      <div className="menu">
        <Head />
        <div style={{ display: "flex", justifyContent: "center", margin: "10px 0 16px" }}>
          <Mascot mood="loading" quip={resolveCopy("menu.loading") ?? ""} />
        </div>
        <div className="menu__list" aria-hidden="true">
          <Skeleton height={300} radius={22} />
          <Skeleton height={300} radius={22} />
        </div>
      </div>
    );
  }

  if (picks.length === 0) {
    return (
      <div className="deck--center">
        <Card padding="lg" className="menu__empty">
          <p className="h-caps menu__kicker">Tonight&apos;s picks</p>
          <h1 className="type-display-3" style={{ marginBottom: 10 }}>
            Today&apos;s Menu
          </h1>
          <Mascot mood="ko" quip={resolveCopy("menu.empty") ?? ""} />
          <p className="menu__empty-count">
            Kitchen reopens in{" "}
            <span className="menu__countdown-time type-numeric">{midnightCountdown()}</span>
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="menu">
      <Head />
      <motion.div className="menu__list" variants={stagger} initial="initial" animate="enter">
        <AnimatePresence mode="popLayout">
          {picks.map((card, i) => {
            const id = card.profile?.user?.id ?? "";
            return (
              <motion.div
                key={id}
                className="menu__course"
                variants={rise}
                exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.16 } }}
                layout
              >
                <span className="menu__course-no" aria-hidden="true">
                  N° {i + 1}
                </span>
                <div className="deck__card">
                  <CardFace card={card} />
                  <div className="menu__actions">
                    <Button
                      variant="reject"
                      size="sm"
                      style={{ flex: 1 }}
                      disabled={busy}
                      onClick={() => void act(card, "reject")}
                    >
                      ✕ {resolveCopy("deck.swipe_labels.left")}
                    </Button>
                    <Button
                      size="sm"
                      disabled={busy}
                      title={resolveCopy("deck.crush_button_tooltip")}
                      onClick={() => void act(card, "crush")}
                    >
                      ⭐
                    </Button>
                    <Button
                      variant="like"
                      size="sm"
                      style={{ flex: 1 }}
                      disabled={busy}
                      onClick={() => void act(card, "like")}
                    >
                      ♥ {resolveCopy("deck.swipe_labels.right")}
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      <Sheet open={reveal !== null} onClose={() => setReveal(null)}>
        {reveal && (
          <div className="reveal">
            <h2 className="h-display reveal__title">
              {reveal.reveal?.copy_key
                ? resolveCopy(reveal.reveal.copy_key)
                : resolveCopy("match.classic_title")}
            </h2>
            <Button variant="primary" block onClick={() => setReveal(null)}>
              Keep browsing
            </Button>
          </div>
        )}
      </Sheet>
    </div>
  );
}

/** Display-only countdown to LOCAL midnight (the server refresh is the truth). */
function midnightCountdown(): string {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  const ms = midnight.getTime() - now.getTime();
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
